import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { ConfirmActionModalHostDirective } from '../../directives/confirm-action-modal-host/confirm-action-modal-host.directive';
import { ModalConfirmActionComponent } from '../../components/modal-confirm-action/modal-confirm-action.component';
import { ConfirmActionModalButton, ConfirmActionModalOptions } from '../../models/confirm-action-modal-options.model';
import { firstValueFrom, Observable, Subject, take, takeUntil, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConfirmActionModalService implements OnDestroy {
  private modalsHost!: ConfirmActionModalHostDirective;
  private activeModal: ComponentRef<ModalConfirmActionComponent> | null = null;
  private destroy$ = new Subject<void>();

  public setModalHost(host: ConfirmActionModalHostDirective) {
    this.modalsHost = host;
  }

  public showModal(modalOptions?: ConfirmActionModalOptions): Observable<ConfirmActionModalButton | undefined> {
    if (!this.modalsHost) {
      throw new Error(
        'ConfirmActionModalService: Modal host is not initialized. ' +
        'Make sure to add the ConfirmActionModalHostDirective to your app root component ' +
        'and call setModalHost() before using showModal().'
      );
    }

    if (this.activeModal) {
      this.closeModal();
    }

    const modalRef = this.modalsHost.viewContainerRef.createComponent(ModalConfirmActionComponent);
    const modalRefInstance = modalRef.instance;
    if (!modalOptions) {
      modalOptions = {};
    }
    modalRefInstance.modalOptions = modalOptions;
    this.activeModal = modalRef;

    return new Observable<ConfirmActionModalButton | undefined>((observer) => {
      modalRefInstance.userAction
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          tap((confirmActionModalButton) => {
            observer.next(confirmActionModalButton);
            observer.complete();
            this.closeModal();
          }),
        )
        .subscribe({
          error: (err) => {
            observer.error(err);
            this.closeModal();
          }
        });
    });
  }

  public async showConfirmWizardCloseModal(): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.showModal({
          title: 'VORGANG_ABBRECHEN_TITLE',
          infoText: 'VORGANG_ABBRECHEN_TEXT',
          hideCloseButton: true,
          buttons: [
            {
              label: 'WEITER_BEARBEITEN',
              variant: 'primary',
              value: false,
              role: 'cancel',
            },
            {
              label: 'VORGANG_ABBRECHEN',
              variant: 'secondary',
              value: true,
            },
          ],
        }),
      );
      return result?.value === true;
    } catch (error) {
      // Handle empty sequence or other errors gracefully
      return false;
    }
  }

  public closeModal() {
    if (this.activeModal) {
      this.activeModal.destroy();
      this.activeModal = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeModal();
  }
}
